// File: src/EnglishTool/SpeakButton.jsx

import React, { useState, useEffect } from "react";

const CHARACTER_VOICE_HINTS = {
  "A Midsummer Night’s Dream": {
    Puck:      { gender: "male", nameHint: "David" },
    Oberon:    { gender: "male", nameHint: "Daniel" },
    Titania:   { gender: "female", nameHint: "Samantha" },
    Hermia:    { gender: "female", nameHint: "Karen" },
    Lysander:  { gender: "male", nameHint: "James" },
    Demetrius: { gender: "male", nameHint: "Alex" },
    Helena:    { gender: "female", nameHint: "Victoria" }
  }
  // Add more plays/characters as needed.
};

function findVoice(character, play, availableVoices) {
  if (!character || !play || !availableVoices.length) return null;
  const hint = CHARACTER_VOICE_HINTS[play]?.[character];
  if (!hint) return null;
  // Try matching by name
  let found = null;
  if (hint.nameHint) {
    found = availableVoices.find(
      v =>
        v.name.toLowerCase().includes(hint.nameHint.toLowerCase()) &&
        (!hint.gender || v.gender?.toLowerCase() === hint.gender)
    );
    if (found) return found;
  }
  // Try matching by gender
  if (hint.gender) {
    found = availableVoices.find(
      v => v.gender && v.gender.toLowerCase() === hint.gender
    );
    if (found) return found;
  }
  // Fallback
  return null;
}

export default function SpeakButton({ text, character, play }) {
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    function updateVoices() {
      const vs = window.speechSynthesis.getVoices();
      setVoices(vs);
      // For debugging only:
      // console.log(vs.map(v => `${v.name} (${v.lang}) - ${v.gender}`));
    }
    updateVoices();
    window.speechSynthesis.onvoiceschanged = updateVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  function handleClick() {
    if (!window.speechSynthesis) return;
    const utterance = new window.SpeechSynthesisUtterance(text);

    let selectedVoice = null;
    // Try Shakespeare character voice
    if (character && play) {
      selectedVoice = findVoice(character, play, voices);
    }

    // Fallback to "en-GB" or "en-US" English if no character/play match
    if (!selectedVoice) {
      selectedVoice =
        voices.find(v => v.lang === "en-GB" && v.gender === "male") ||
        voices.find(v => v.lang === "en-US" && v.gender === "female") ||
        voices.find(v => v.lang && v.lang.startsWith("en-")) ||
        voices[0]; // just pick the first available as last resort
    }
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      // Optional: pitch/rate tweak
      if (character === "Puck") {
        utterance.pitch = 1.55;
        utterance.rate = 1.15;
      } else if (character === "Oberon") {
        utterance.pitch = 0.88;
        utterance.rate = 0.96;
      } else if (character === "Titania") {
        utterance.pitch = 1.33;
        utterance.rate = 1.02;
      } else if (character === "Demetrius" || character === "Lysander") {
        utterance.pitch = 1.1;
        utterance.rate = 1.01;
      } else if (character === "Hermia" || character === "Helena") {
        utterance.pitch = 1.2;
        utterance.rate = 1.12;
      }
    }
    window.speechSynthesis.speak(utterance);
  }

  // Button label (shows character if provided)
  let label = "🔊";
  if (character) label += ` ${character}`;
  return (
    <button
      className="button1"
      onClick={handleClick}
      title={character ? `Read as ${character}` : "Read aloud"}
      style={{ marginLeft: 8 }}
    >
      {label}
    </button>
  );
}
